import { useEffect, useState, useImperativeHandle, forwardRef, useRef } from "react";

const buildOrderedOptions = (activeNode, answers) => {
  const nodeOptions = Array.isArray(activeNode?.options) ? activeNode.options : [];
  const currentAnswer = answers?.[activeNode?.id];

  if (Array.isArray(currentAnswer) && currentAnswer.length > 0) {
    const rankedOptions = currentAnswer
      .map((optionId) => nodeOptions.find((option) => option.id === optionId))
      .filter(Boolean);
    const remainingOptions = nodeOptions.filter((option) => !currentAnswer.includes(option.id));
    return [...rankedOptions, ...remainingOptions];
  }

  return nodeOptions;
};

const RankingQuestionInput = forwardRef(
  (
    {
      activeNode,
      answers,
      handleAnswerChange,
      onAnswerChange,
      language,
      i18nValue,
      autoAdvanceEnabled = false,
      colors = {},
    },
    ref
  ) => {
    const {
      questionButtonBackgroundColor = "#82378A",
      questionButtonTextColor = "#FFFFFF",
      questionBorderColor = "#D7DEEA",
      questionSoftBorderColor = "#E4E6EF",
      questionTextColor = "#475569",
      isSimpleInput = false,
      simpleGroupedBorderColor = "#E4E6EF",
      questionInputBackgroundColor = "#FFFFFF",
    } = colors;

    const [draggedIndex, setDraggedIndex] = useState(null);
    const [orderedOptions, setOrderedOptions] = useState(() => buildOrderedOptions(activeNode, answers));
    const touchStartIndexRef = useRef(null);
    const touchStartYRef = useRef(null);
    const touchCurrentIndexRef = useRef(null);

    useEffect(() => {
      setOrderedOptions(buildOrderedOptions(activeNode, answers));
    }, [activeNode, answers]);

    const confirmRanking = () => {
      const rankedOptionIds = orderedOptions.map((opt) => opt.id);
      return rankedOptionIds;
    };

    useImperativeHandle(ref, () => ({
      confirmRanking,
    }));

    const handleDragStart = (index) => {
      setDraggedIndex(index);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (targetIndex) => {
      if (draggedIndex === null || draggedIndex === targetIndex) {
        setDraggedIndex(null);
        return;
      }

      const newOrdered = [...orderedOptions];
      const [draggedItem] = newOrdered.splice(draggedIndex, 1);
      newOrdered.splice(targetIndex, 0, draggedItem);
      setOrderedOptions(newOrdered);

      setDraggedIndex(null);
      if (autoAdvanceEnabled) {
        handleAnswerChange(
          activeNode.id,
          newOrdered.map((option) => option.id)
        );
      }
      if (onAnswerChange) {
        onAnswerChange();
      }
    };

    const handlePositionChange = (currentIndex, newIndex) => {
      if (newIndex === currentIndex || newIndex < 0 || newIndex >= orderedOptions.length) {
        return;
      }

      const newOrdered = [...orderedOptions];
      const [movedItem] = newOrdered.splice(currentIndex, 1);
      newOrdered.splice(newIndex, 0, movedItem);
      setOrderedOptions(newOrdered);
      if (autoAdvanceEnabled) {
        handleAnswerChange(
          activeNode.id,
          newOrdered.map((option) => option.id)
        );
      }
      if (onAnswerChange) {
        onAnswerChange();
      }
    };

    const handleTouchStart = (index, e) => {
      touchStartIndexRef.current = index;
      touchStartYRef.current = e.touches[0].clientY;
      touchCurrentIndexRef.current = index;
      setDraggedIndex(index);
    };

    const handleTouchMove = (e) => {
      if (touchStartIndexRef.current === null) return;
      
      const touchY = e.touches[0].clientY;
      const deltaY = touchY - touchStartYRef.current;
      const itemHeight = e.currentTarget.offsetHeight;
      
      const threshold = itemHeight / 3;
      
      if (Math.abs(deltaY) > threshold) {
        const direction = deltaY > 0 ? 1 : -1;
        const newIndex = touchCurrentIndexRef.current + direction;
        
        if (newIndex >= 0 && newIndex < orderedOptions.length && newIndex !== touchCurrentIndexRef.current) {
          const newOrdered = [...orderedOptions];
          const [movedItem] = newOrdered.splice(touchCurrentIndexRef.current, 1);
          newOrdered.splice(newIndex, 0, movedItem);
          setOrderedOptions(newOrdered);
          
          touchCurrentIndexRef.current = newIndex;
          touchStartYRef.current = touchY;
        }
      }
    };

    const handleTouchEnd = () => {
      if (touchStartIndexRef.current === null) return;
      
      const startIndex = touchStartIndexRef.current;
      const endIndex = touchCurrentIndexRef.current;
      
      touchStartIndexRef.current = null;
      touchStartYRef.current = null;
      setDraggedIndex(null);
      
      if (startIndex !== endIndex && autoAdvanceEnabled) {
        handleAnswerChange(
          activeNode.id,
          orderedOptions.map((option) => option.id)
        );
      }
      
      if (onAnswerChange) {
        onAnswerChange();
      }
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {orderedOptions.map((option, index) => {
          const optionLabel = i18nValue(option, "label", language, null, option.label);
          return (
            <div
              key={option.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              onDragEnd={() => setDraggedIndex(null)}
              onTouchStart={(e) => handleTouchStart(index, e)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className="ranking-item d-flex align-items-center text-left border-0 w-100"
              style={{
                gap: 10,
                padding: "14px 16px",
                borderRadius: 12,
                border: `1px solid ${draggedIndex === index ? questionButtonBackgroundColor : isSimpleInput ? simpleGroupedBorderColor : questionSoftBorderColor}`,
                background: draggedIndex === index ? `${questionButtonBackgroundColor}10` : questionInputBackgroundColor,
                fontSize: 15,
                color: questionTextColor,
                boxShadow: draggedIndex === index ? `0 8px 20px ${questionButtonBackgroundColor}22` : "0 1px 3px rgba(15,23,42,0.06)",
                cursor: "move",
                transition: "all 200ms ease",
                opacity: draggedIndex === index ? 0.6 : 1,
              }}
            >
              {/* TODO: Add ranking position selector */}
              {/* <div style={{ minWidth: "50px", display: "flex", alignItems: "center" }}>
                <select
                  value={index}
                  onChange={(e) => {
                    const newIndex = Number(e.target.value);
                    if (newIndex !== index) {
                      handlePositionChange(index, newIndex);
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    fontSize: "13px",
                    padding: "6px 8px",
                    borderRadius: "6px",
                    border: `1px solid ${questionBorderColor}`,
                    background: questionInputBackgroundColor,
                    color: questionTextColor,
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  {orderedOptions.map((_, idx) => (
                    <option key={idx} value={idx}>
                      {idx + 1}
                    </option>
                  ))}
                </select>
              </div> */}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: questionTextColor,
                  opacity: 0.5,
                  fontSize: 16,
                }}
              >
                <i className="fa fa-bars" />
              </div>

              <span style={{ flex: 1 }}>{optionLabel}</span>
            </div>
          );
        })}
      </div>
    );
  }
);

RankingQuestionInput.displayName = "RankingQuestionInput";

export default RankingQuestionInput;
